import type { ComputedRef } from 'vue'
import type { SubscriptionModal } from '@entities/subscription'
import type { CustomerActiveSubscription } from '@entities/customer'

import { useSubscriptionsStore, useSubscriptionsApi } from '@widgets/subscriptions'
import { useCustomerStore } from '@entities/customer'

import { useCustomerPrivileges } from './customer-privileges.hook'

interface CustomerSubscriptionsReturn {
  refSubscriptions: ComputedRef<CustomerActiveSubscription[]>
  activeModal: ComputedRef<SubscriptionModal | null>
  canManageSubscriptions: ComputedRef<boolean>
}

/**
 * The purchase cards reuse the Subscriptions page actions and modals. Those resolve the subscription
 * from the subscriptions store and refetch it after a write, so the store is narrowed to this customer
 * and the cards prefer its fresh copy over the one `customers.details` returned.
 */
export const useCustomerSubscriptions = (): CustomerSubscriptionsReturn => {
  const route = useRoute()
  const customerStore = useCustomerStore()
  const subscriptionsStore = useSubscriptionsStore()
  const { canViewSubscriptions, canManageSubscriptions } = useCustomerPrivileges()

  const refSubscriptions = computed<CustomerActiveSubscription[]>(() =>
    (customerStore.getDetails?.active_subscriptions ?? []).map((subscription) => {
      const fresh = subscriptionsStore.getSubscriptions.find(item => item.id === subscription.id)

      return fresh ? { ...subscription, ...fresh, pricing: { ...subscription.pricing, ...fresh.pricing } } : subscription
    }))

  const activeModal = computed<SubscriptionModal | null>(() =>
    (subscriptionsStore.getModalSubscription ? subscriptionsStore.getActiveModal : null))

  onMounted(() => {
    if (!canViewSubscriptions.value) return

    subscriptionsStore.resetPageState()
    subscriptionsStore.resetFilters()
    subscriptionsStore.setFilterCustomerId(Number(route.params.id))
    void useSubscriptionsApi()
  })

  onBeforeUnmount(() => {
    subscriptionsStore.resetPageState()
  })

  return {
    refSubscriptions,
    activeModal,
    canManageSubscriptions,
  }
}
